import React from 'react';

import type * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableLayout,
    DataTableRoot,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Pagination,
    Panel,
    PanelBody,
} from '@gitbook/spine-react';

import { apiResource, useSuspensePaginatedAPIQuery } from '../../state';
import {
    ConversationIssueDialog,
    type ConversationIssueDialogProps,
} from './ConversationIssueDialog';
import { ConversationIssueImpactSpan } from './ConversationIssueImpactSpan';
import { ConversationSubject } from './ConversationSubject';

type ConversationIssuesTableProps = {
    organizationId: string;

    /** If provided, only issues from the cluster will be displayed */
    clusterId?: string;

    /** If provided, only issues from the conversation will be displayed */
    conversationId?: string;
} & Pick<ConversationIssueDialogProps, 'withClusterRelation' | 'withConversationRelation'>;

/**
 * Table to display the conversation issues.
 */
export function ConversationIssuesTable(props: ConversationIssuesTableProps) {
    const {
        organizationId,
        clusterId,
        conversationId,
        withClusterRelation = true,
        withConversationRelation = true,
    } = props;

    const { data, next, previous, range } = useSuspensePaginatedAPIQuery(
        apiResource('listConversationsIssues', {
            organizationId,
            cluster: clusterId,
            conversation: conversationId,
        })
    );

    const columns = React.useMemo<
        DataTableColumns<{
            issue: api.ConversationIssue;
            conversation: api.Conversation;
        }>
    >(() => {
        return [
            {
                key: 'subject',
                label: 'Issue',
                isRowHeader: true,
                cell: ({ item }) => (
                    <div>
                        <div className="text-secondary text-ui-action">{item.issue.title}</div>
                        <div className="text-muted text-ui-small">
                            <Icon.ArrowCurveDownRight className="mr-3 size-4" />
                            <ConversationSubject conversation={item.conversation} />
                        </div>
                    </div>
                ),
            },
            {
                key: 'impact',
                label: 'Impact',
                cell: ({ item }) => <ConversationIssueImpactSpan impact={item.issue.impact} />,
            },
            {
                key: 'ingestedAt',
                label: 'Ingested at',
                alignment: 'end',
                cell: ({ item }) => (
                    <div className="text-muted text-ui-small">
                        <DateShortRelative value={item.issue.ingestedAt} onlyDate={false} />
                    </div>
                ),
            },
            {
                key: 'actions',
                label: 'Actions',
                alignment: 'end',
                width: 100,
                cell: ({ item }) => (
                    <div className="flex justify-end">
                        <DialogRoot>
                            <DialogTrigger>
                                <Button
                                    kind="blank"
                                    label="Inspect the issue"
                                    iconOnly
                                    leadingIcon={Icon.Maximize}
                                />
                            </DialogTrigger>
                            <ConversationIssueDialog
                                organizationId={organizationId}
                                issueId={item.issue.id}
                                withClusterRelation={withClusterRelation}
                                withConversationRelation={withConversationRelation}
                            />
                        </DialogRoot>
                    </div>
                ),
            },
        ];
    }, [organizationId, withClusterRelation, withConversationRelation]);

    if (data.items.length === 0) {
        return (
            <Panel>
                <PanelBody>
                    <EmptyState>
                        <EmptyStateIcon>
                            <Icon.Comment />
                        </EmptyStateIcon>
                        <Heading>No issues identified</Heading>
                        <Description>
                            No issue has been identified yet from conversations.
                        </Description>
                    </EmptyState>
                </PanelBody>
            </Panel>
        );
    }

    return (
        <DataTableRoot>
            <DataTableLayout>
                <DataTable getKey={getItemKey} columns={columns} items={data.items} />
                <DataTableFooter>
                    <Pagination range={range} next={next} previous={previous} />
                </DataTableFooter>
            </DataTableLayout>
        </DataTableRoot>
    );
}

function getItemKey(item: { issue: api.ConversationIssue; conversation: api.Conversation }) {
    return item.issue.id;
}
